import { createRouter, createWebHistory } from 'vue-router';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/firebase.js';
import { v2Routes } from '@/router/v2Routes';
import { legacyAliases } from '@/router/legacyAliases';
import { isFeatureEnabled } from '@/config/featureFlags';

// Main Pages
import HomeMain from '@/pages/Home.vue';
import LoginMain from '@/pages/Login.vue';
import ResetPassword from '@/pages/ResetPassword.vue';
import ConfirmResetPassword from '@/pages/ConfirmResetPassword.vue';

// Device Pages
import MainDashboard from '@/pages/devices/MainDashboard.vue';
import DashboardPage from '@/pages/devices/DashboardPage.vue';
import UnitDashboardManagement from '@/pages/devices/UnitDashboardManagement.vue';
import CloudAppPortal from '@/pages/devices/CloudAppPortal.vue';

// App Pages
import XetaSense from '@/apps/XetaSense.vue';
import BluetoothDash from '@/apps/TdwlbSensor.vue';

// Sim Cards Pages
import SimDashboard from '@/pages/simCards/SimDashboard.vue';

// Settings Pages
import SettingsMenu from '@/pages/settings/SettingsMenu.vue';

const legacyRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginMain,
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
  },
  {
    path: '/auth/action',
    name: 'ConfirmResetPassword',
    component: ConfirmResetPassword,
    // no requiresAuth — user arrives here unauthenticated
  },
  {
    path: '/',
    name: 'Home',
    component: HomeMain,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: MainDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsMenu,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/:dataID/p/:pageId',
    name: 'DashboardPage',
    component: DashboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/:dataID/xetasense',
    name: 'XetaSense',
    component: XetaSense,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/:dataID/pressure-sensor',
    name: 'Bluetooth',
    component: BluetoothDash,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/:dataID/management',
    name: 'UnitDashboadManagement',
    component: UnitDashboardManagement,
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/:dataID/portal',
    name: 'CloudAppPortal',
    component: CloudAppPortal,
    meta: { requiresAuth: true },
  },
  {
    path: '/sim-cards',
    name: 'SimCards',
    component: SimDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: '/applications',
    name: 'CloudApps',
    component: () => import('@/pages/cloudApps/CloudAppsPage.vue'),
    meta: { requiresAuth: true },
  },
  // other routes...
];

const routes = [...legacyRoutes, ...v2Routes, ...legacyAliases];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });
};

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const featureFlagRecord = to.matched.find((record) => record.meta?.featureFlag);
  const featureFallbackRoutes = {
    optionCOperateV2: { name: 'Dashboard' },
    optionCManageV2: { name: 'Settings' },
    optionCAnalyzeV2: { name: 'CloudApps' },
    optionCAdminV2: { name: 'Settings' },
    optionCToolsV2: { name: 'CloudApps' },
  };

  try {
    const user = await getCurrentUser();
    if (requiresAuth && !user) {
      next({ name: 'Login' });
      return;
    }

    if (featureFlagRecord) {
      const flagName = featureFlagRecord.meta.featureFlag;
      if (!isFeatureEnabled(flagName)) {
        next(featureFallbackRoutes[flagName] || { name: 'Home' });
        return;
      }
    }

    next();
  } catch (error) {
    console.error('Auth check failed', error);
    next({ name: 'Login' });
  }
});

export default router;
